"use client";

import {
  Box,
  Center,
  Flex,
  Heading,
  Icon,
  List,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IconType } from "react-icons";

interface PolicySectionCardProps {
  sectionId: string;
  icon: IconType;
  number: number;
  sectionTitle?: string;
  children: React.ReactNode;
}

export function PolicySectionCard({
  sectionId,
  icon,
  number,
  sectionTitle,
  children,
}: PolicySectionCardProps) {
  return (
    <Box
      id={sectionId}
      bg="white"
      borderRadius="xl"
      p={{ base: 5, md: 7 }}
      border="1px solid"
      borderColor="neutral.100"
      shadow="0 1px 3px rgba(0,0,0,0.04)"
      transition="all 0.2s ease"
      _hover={{
        shadow: "0 4px 12px rgba(0,0,0,0.06)",
        borderColor: "neutral.200",
      }}
    >
      <Flex align="center" gap={3} mb={4}>
        <Center w={9} h={9} borderRadius="lg" bg="primary.50">
          <Icon as={icon} boxSize={4.5} color="primary.500" />
        </Center>
        <VStack alignItems="start" gap={0}>
          <Text
            textStyle="2xs"
            fontWeight="light"
            color="primary.400"
            fontFamily="heading"
            letterSpacing="0.02em"
            lineHeight={1.2}
          >
            SECTION {number}
          </Text>
          {sectionTitle ? (
            <Heading
              textStyle="md"
              fontWeight="bold"
              color="neutral.900"
              fontFamily="heading"
              letterSpacing="0.02em"
              lineHeight={1.3}
            >
              {sectionTitle}
            </Heading>
          ) : null}
        </VStack>
      </Flex>
      {children}
    </Box>
  );
}

export function PolicyIntroCard({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      id={id}
      bg="primary.50/50"
      borderRadius="xl"
      p={{ base: 5, md: 7 }}
      mb={8}
      border="1px solid"
      borderColor="primary.100"
    >
      <Text textStyle="sm" color="neutral.700" lineHeight={1.8} mb={0}>
        {children}
      </Text>
    </Box>
  );
}

export function PolicyText({ children }: { children: React.ReactNode }) {
  return (
    <Text textStyle="sm" color="neutral.600" lineHeight={1.8} mb={5}>
      {children}
    </Text>
  );
}

export function PolicySubheading({ children }: { children: React.ReactNode }) {
  return (
    <Text textStyle="sm" fontWeight="bold" color="neutral.700" mb={3}>
      {children}
    </Text>
  );
}

export function PolicyList({
  items,
  ordered = false,
}: {
  items: string[];
  ordered?: boolean;
}) {
  return (
    <List.Root
      as={ordered ? "ol" : "ul"}
      listStyleType={ordered ? "decimal" : "disc"}
      pl={6}
      mb={5}
      color="neutral.600"
    >
      {items.map((item) => (
        <List.Item key={item} textStyle="sm" lineHeight={1.8} mb={2}>
          {item}
        </List.Item>
      ))}
    </List.Root>
  );
}
